/**
 * Booking Details View
 * Full-page view for viewing and editing booking details
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  CalendarToday,
  AccessTime,
  Group,
  LocationOn,
  CheckCircle,
  Message,
  Download,
  FileDownload,
} from "@mui/icons-material";
import { format } from "date-fns";
import { getBooking } from "../../quotes/services/bookingService";
import BookingConfirmationTemplatesModal from "../components/BookingConfirmationTemplatesModal";
import EditBookingModal from "../components/EditBookingModal";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import { useAuth } from "../../../contexts/AuthContext";
import { orderDownloadService } from "../../orders/services/downloadService.js";
import CustomerNotesPanel from "../../customers/components/CustomerNotesPanel.jsx";

/**
 * BookingDetailsView Component
 */
const BookingDetailsView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get source information from location state
  const source = location.state?.source; // 'quote' or 'order'
  const sourceId = location.state?.sourceId; // quoteId or orderId
  const sourcePath = location.state?.sourcePath; // full path to return to

  // Get initial booking data from location state (optimistic update)
  const initialBooking = location.state?.booking;

  // State
  const [booking, setBooking] = useState(initialBooking || null);
  const [loading, setLoading] = useState(!initialBooking); // Don't show loading if we have initial data
  const [error, setError] = useState(null);
  const [templatesModalVisible, setTemplatesModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch booking data
  const fetchBooking = useCallback(async () => {
    if (!orderId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getBooking(orderId);
      if (response.success) {
        setBooking(response.data);
      } else {
        setError(response.error || "Failed to load booking");
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError(err.message || "Failed to load booking");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  // Mobile "Assign Driver" deep-link: open edit modal once booking is loaded
  useEffect(() => {
    if (location.state?.openEdit && booking && !loading) {
      setEditModalVisible(true);
    }
  }, [location.state?.openEdit, booking, loading]);

  // Handlers
  const handleBack = useCallback(() => {
    // Return to source entity if available
    if (sourcePath) {
      navigate(sourcePath);
    } else if (source === "quote" && sourceId) {
      navigate(`/quotes/${sourceId}`);
    } else if (source === "order" && sourceId) {
      navigate("/orders");
    } else {
      // Default fallback
      navigate("/quotes");
    }
  }, [navigate, source, sourceId, sourcePath]);

  // Determine back button text
  const getBackButtonText = () => {
    if (source === "quote" && sourceId) {
      return `Back to Quote ${sourceId}`;
    } else if (source === "order") {
      return "Back to Orders";
    }
    return "Back to Quotes";
  };

  const handleViewTemplates = () => {
    setTemplatesModalVisible(true);
  };

  const handleEdit = () => {
    setEditModalVisible(true);
  };

  const handleEditSuccess = async () => {
    // Wait a bit for database write to complete before refreshing
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Refresh booking data
    await fetchBooking();

    setNotification({
      open: true,
      message: "Booking updated successfully",
      severity: "success",
    });
  };

  const handleDownloadDriverPdf = useCallback(async () => {
    if (!booking?.order?.id) {
      setNotification({
        open: true,
        message: "Order ID not available",
        severity: "error",
      });
      return;
    }
    try {
      await orderDownloadService.downloadDriverPickupPdf(booking.order.id);
      setNotification({
        open: true,
        message: "Driver pickup PDF downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download driver pickup PDF",
        severity: "error",
      });
    }
  }, [booking?.order?.id]);

  const handleDownloadClientPdf = useCallback(async () => {
    if (!booking?.order?.id) {
      setNotification({
        open: true,
        message: "Order ID not available",
        severity: "error",
      });
      return;
    }
    try {
      await orderDownloadService.downloadClientPickupPdf(booking.order.id);
      setNotification({
        open: true,
        message: "Client pickup PDF downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download client pickup PDF",
        severity: "error",
      });
    }
  }, [booking?.order?.id]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return format(new Date(date), "dd MMM yyyy");
  };

  const formatTimeWindow = (timeWindow) => {
    if (!timeWindow) return "N/A";
    if (typeof timeWindow === "string") return timeWindow;
    if (timeWindow.start && timeWindow.end) {
      return `${timeWindow.start} - ${timeWindow.end}`;
    }
    return "N/A";
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "default",
      confirmed: "success",
      in_progress: "info",
      completed: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  // Format phone number for WhatsApp link
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, "");
    // If starts with 0, replace with 27
    if (digits.startsWith("0")) {
      return `27${digits.substring(1)}`;
    }
    // If starts with 27, use as is
    if (digits.startsWith("27")) {
      return digits;
    }
    // If starts with +27, remove the +
    if (phone.startsWith("+27")) {
      return digits;
    }
    // Otherwise, assume it's a local number and add 27
    return `27${digits}`;
  };

  const handleWhatsAppClick = (phone) => {
    const formattedPhone = formatPhoneForWhatsApp(phone);
    if (formattedPhone) {
      const whatsappUrl = `https://wa.me/${formattedPhone}`;
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Page structure shows immediately - show loading/error states inline
  const bookingData = booking?.booking;
  const order = booking?.order;
  const customerId = order?.customerId || booking?.customerId;

  return (
    <Container
      maxWidth="lg"
      sx={{
        // Add bottom padding on mobile to prevent overlap with fixed elements
        pb: { xs: 3, md: 0 },
        position: "relative",
      }}
    >
      <Box py={3}>
        {/* Error state - show inline */}
        {error && !booking && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading state - show inline */}
        {loading && !booking && (
          <Box
            py={3}
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            mb={3}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Not found state - show inline */}
        {!loading && !error && (!booking || !booking.booking) && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Booking not found
            </Alert>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              variant="outlined"
            >
              {getBackButtonText()}
            </Button>
          </Box>
        )}

        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            {getBackButtonText()}
          </Button>
          <Typography variant="h4" component="h1">
            Booking Details
          </Typography>
        </Box>

        {/* Actions */}
        <Box
          display="flex"
          gap={2}
          mb={3}
          flexWrap="wrap"
          sx={{
            // Ensure proper spacing on mobile to prevent overlap
            position: "relative",
            zIndex: 1,
            // Add bottom padding on mobile if needed
            pb: { xs: 0, md: 0 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEdit}
            color="primary"
          >
            Edit Booking
          </Button>
          <Button
            variant="contained"
            startIcon={<Message />}
            onClick={handleViewTemplates}
            color="primary"
          >
            View Confirmation Templates
          </Button>
          {order?.id && (
            <>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadDriverPdf}
                color="primary"
              >
                Driver Pickup PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleDownloadClientPdf}
                color="primary"
              >
                Client Pickup PDF
              </Button>
            </>
          )}
        </Box>

        {/* Show content only when booking is loaded */}
        {bookingData && order ? (
          <Grid container spacing={3}>
            {/* Left Column - Booking Info */}
            <Grid item xs={12} md={8}>
              {/* Booking Status */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">Booking Status</Typography>
                    <Chip
                      label={bookingData.status || "scheduled"}
                      color={getStatusColor(bookingData.status)}
                      size="medium"
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Move Date
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="medium">
                        {formatDate(bookingData.moveDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Time Window
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="medium">
                        {formatTimeWindow(bookingData.pickupTimeWindow)}
                      </Typography>
                    </Grid>
                    {bookingData.assignedTeam && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                          >
                            <Group fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Assigned Team
                            </Typography>
                          </Box>
                          <Typography variant="body1" fontWeight="medium">
                            {bookingData.assignedTeam.teamName || "TBD"}
                          </Typography>
                          {bookingData.assignedTeam.vehicle?.licensePlate && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Vehicle:{" "}
                              {bookingData.assignedTeam.vehicle.licensePlate} (
                              {bookingData.assignedTeam.vehicle.vehicleType})
                            </Typography>
                          )}
                        </Grid>
                        {bookingData.assignedTeam.primaryDriver && (
                          <Grid item xs={12}>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                bgcolor: "primary.light",
                                color: "primary.contrastText",
                                // Add margin bottom on mobile to prevent overlap with buttons below
                                mb: { xs: 2, md: 0 },
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={1}
                              >
                                <Message fontSize="small" />
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  CONTACT DURING YOUR MOVE
                                </Typography>
                              </Box>
                              <Box
                                display="flex"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                gap={1}
                                flexWrap="wrap"
                                sx={{
                                  mb: 1,
                                  // Ensure proper spacing on mobile
                                  "& > *": {
                                    flexShrink: 0,
                                  },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  fontWeight="medium"
                                  sx={{
                                    // Allow text to wrap on mobile, take full width
                                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                    minWidth: 0,
                                    // Add margin bottom on mobile to separate from WhatsApp button
                                    mb: { xs: 1, sm: 0 },
                                  }}
                                >
                                  Contact{" "}
                                  {bookingData.assignedTeam.primaryDriver.name}{" "}
                                  at{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {
                                      bookingData.assignedTeam.primaryDriver
                                        .phone
                                    }
                                  </Box>
                                </Typography>
                                <Tooltip title="Send WhatsApp message">
                                  <IconButton
                                    onClick={() =>
                                      handleWhatsAppClick(
                                        bookingData.assignedTeam.primaryDriver
                                          .phone
                                      )
                                    }
                                    sx={{
                                      color: "primary.contrastText",
                                      bgcolor: "rgba(255, 255, 255, 0.2)",
                                      "&:hover": {
                                        bgcolor: "rgba(255, 255, 255, 0.3)",
                                      },
                                      // Ensure button doesn't overlap on mobile
                                      position: "relative",
                                      zIndex: 1,
                                      // On mobile, position it on its own line
                                      alignSelf: {
                                        xs: "flex-start",
                                        sm: "center",
                                      },
                                    }}
                                    size="small"
                                    aria-label="Send WhatsApp"
                                  >
                                    <Message fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="body2">
                                He is your point of contact on the road.
                              </Typography>
                              {bookingData.assignedTeam.primaryDriver
                                .ownerType === "third_party" && (
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", mt: 1, opacity: 0.9 }}
                                >
                                  3rd Party Driver
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Addresses */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Addresses
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationOn fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Pickup Address
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {order?.pickup?.address || "N/A"}
                      </Typography>
                      {order?.pickup?.addressComponents && (
                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary">
                            {order.pickup.addressComponents.streetNumber}{" "}
                            {order.pickup.addressComponents.streetName},{" "}
                            {order.pickup.addressComponents.suburb},{" "}
                            {order.pickup.addressComponents.town},{" "}
                            {order.pickup.addressComponents.province}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationOn fontSize="small" color="secondary" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Delivery Address
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {order?.delivery?.address || "N/A"}
                      </Typography>
                      {order?.delivery?.addressComponents && (
                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary">
                            {order.delivery.addressComponents.streetNumber}{" "}
                            {order.delivery.addressComponents.streetName},{" "}
                            {order.delivery.addressComponents.suburb},{" "}
                            {order.delivery.addressComponents.town},{" "}
                            {order.delivery.addressComponents.province}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Special Notes */}
              {bookingData.notes && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Special Notes
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      {bookingData.notes.stairs && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Stairs
                          </Typography>
                          <Typography variant="body1">
                            {bookingData.notes.stairs}
                          </Typography>
                        </Grid>
                      )}
                      {bookingData.notes.accessRules && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Access Rules
                          </Typography>
                          <Typography variant="body1">
                            {bookingData.notes.accessRules}
                          </Typography>
                        </Grid>
                      )}
                      {bookingData.notes.fragileItems && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Fragile Items
                          </Typography>
                          <Typography variant="body1">
                            {bookingData.notes.fragileItems}
                          </Typography>
                        </Grid>
                      )}
                      {bookingData.notes.gateCode && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Gate Code
                          </Typography>
                          <Typography variant="body1">
                            {bookingData.notes.gateCode}
                          </Typography>
                        </Grid>
                      )}
                      {bookingData.notes.estateName && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Estate Name
                          </Typography>
                          <Typography variant="body1">
                            {bookingData.notes.estateName}
                          </Typography>
                        </Grid>
                      )}
                      {bookingData.notes.contactOnDay && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Contact on Day
                          </Typography>
                          <Typography variant="body1">
                            {bookingData.notes.contactOnDay}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Customer Notes */}
              {customerId && (
                <Box mb={3}>
                  <ConfigProvider theme={bubakiiTheme}>
                    <CustomerNotesPanel
                      customerId={customerId}
                      context="booking"
                      relatedId={orderId}
                      entityType="booking"
                    />
                  </ConfigProvider>
                </Box>
              )}
            </Grid>

            {/* Right Column - Confirmation Status */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Confirmation Status
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">WhatsApp</Typography>
                      {bookingData.confirmations?.whatsappSent ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Sent"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip label="Not Sent" color="default" size="small" />
                      )}
                    </Box>
                    {bookingData.confirmations?.whatsappSentAt && (
                      <Typography variant="caption" color="text.secondary">
                        Sent:{" "}
                        {formatDate(bookingData.confirmations.whatsappSentAt)}
                      </Typography>
                    )}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">Email</Typography>
                      {bookingData.confirmations?.emailSent ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Sent"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip label="Not Sent" color="default" size="small" />
                      )}
                    </Box>
                    {bookingData.confirmations?.emailSentAt && (
                      <Typography variant="caption" color="text.secondary">
                        Sent:{" "}
                        {formatDate(bookingData.confirmations.emailSentAt)}
                      </Typography>
                    )}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">Reminder</Typography>
                      {bookingData.confirmations?.reminderSent ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Sent"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip label="Not Sent" color="default" size="small" />
                      )}
                    </Box>
                    {bookingData.confirmations?.reminderSentAt && (
                      <Typography variant="caption" color="text.secondary">
                        Sent:{" "}
                        {formatDate(bookingData.confirmations.reminderSentAt)}
                      </Typography>
                    )}
                  </Box>
                  {bookingData.confirmations?.whatsappSent &&
                    bookingData.confirmations?.emailSent && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Booking confirmed - Both WhatsApp and Email sent
                      </Alert>
                    )}
                </CardContent>
              </Card>

              {/* Team Details */}
              {bookingData.assignedTeam && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Team & Vehicle Details
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    {/* All Drivers */}
                    {bookingData.assignedTeam.drivers &&
                      bookingData.assignedTeam.drivers.length > 0 && (
                        <Box mb={2}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            All Drivers
                          </Typography>
                          {bookingData.assignedTeam.drivers.map(
                            (driver, index) => (
                              <Box
                                key={driver.driverId || index}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  bgcolor:
                                    driver.role === "primary"
                                      ? "primary.light"
                                      : "grey.100",
                                  borderRadius: 1,
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {driver.name}
                                      {driver.role === "primary" && (
                                        <Chip
                                          label="Primary"
                                          color="primary"
                                          size="small"
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {driver.phone}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={
                                      driver.role === "primary"
                                        ? "Primary"
                                        : "Helper"
                                    }
                                    size="small"
                                    color={
                                      driver.role === "primary"
                                        ? "primary"
                                        : "default"
                                    }
                                  />
                                </Box>
                              </Box>
                            )
                          )}
                        </Box>
                      )}

                    {/* Vehicle Details */}
                    {bookingData.assignedTeam.vehicle && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Vehicle
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {bookingData.assignedTeam.vehicle.licensePlate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type:{" "}
                            {bookingData.assignedTeam.vehicle.vehicleType?.toUpperCase() ||
                              "N/A"}
                          </Typography>
                          {bookingData.assignedTeam.vehicle.ownerType && (
                            <Box mt={0.5}>
                              <Chip
                                label={
                                  bookingData.assignedTeam.vehicle.ownerType ===
                                  "bubakii"
                                    ? "Bubakii Owned"
                                    : "3rd Party"
                                }
                                size="small"
                                color={
                                  bookingData.assignedTeam.vehicle.ownerType ===
                                  "bubakii"
                                    ? "primary"
                                    : "warning"
                                }
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Team Ownership */}
                    {bookingData.assignedTeam.ownerType && (
                      <Box mt={2}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Team Ownership
                        </Typography>
                        <Chip
                          label={
                            bookingData.assignedTeam.ownerType === "bubakii"
                              ? "Bubakii Team"
                              : bookingData.assignedTeam.ownerType ===
                                  "third_party"
                                ? "3rd Party Team"
                                : "Mixed Team"
                          }
                          color={
                            bookingData.assignedTeam.ownerType === "bubakii"
                              ? "primary"
                              : bookingData.assignedTeam.ownerType ===
                                  "third_party"
                                ? "warning"
                                : "default"
                          }
                          size="small"
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Order Info */}
              {order && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Order Information
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Order ID
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {order.id}
                        </Typography>
                      </Box>
                      {order.quoteId && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Quote ID
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {order.quoteId}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Customer
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {order.customer || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Customer Phone
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {order.customerPhone || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        ) : null}

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Booking Modals */}
      {booking && (
        <ConfigProvider theme={bubakiiTheme}>
          <EditBookingModal
            open={editModalVisible}
            booking={booking}
            order={booking.order}
            onClose={() => setEditModalVisible(false)}
            onSuccess={handleEditSuccess}
          />
          <BookingConfirmationTemplatesModal
            visible={templatesModalVisible}
            orderId={orderId}
            booking={bookingData}
            onClose={() => setTemplatesModalVisible(false)}
            onConfirmationUpdate={() => {
              fetchBooking(); // Refresh booking data
              setNotification({
                open: true,
                message: "Confirmation status updated",
                severity: "success",
              });
            }}
            userId={user?.userId || user?.id}
          />
        </ConfigProvider>
      )}
    </Container>
  );
};

export default BookingDetailsView;
